import React, {useEffect, useState} from 'react';

import cs from 'classnames';
import {Link} from 'react-router';

import {LazyTooltip} from 'app/components/general/LazyTooltip';
import {
  convertLocationParamsToFetchParams,
  useDocuments,
} from 'app/documents/api';
import {StyledCompactItemHeaderSpan} from 'app/general/bootstrap/utils';
import StateButton from 'app/general/widgets/StateButton';
import {DocumentIconWithNotifications} from 'app/notifly/components/DocumentIconWithNotifications';
import {useCreateSuggestions, useSuggestion} from 'app/openai/api';
import {
  extractSuggestionsOfType,
  generateNewDocumentContent,
} from 'app/openai/utils';
import {useCreatePolicy, useGeneratePolicyContent} from 'app/policies/api';
import {createNewProcedure} from 'app/procedures/actions';
import {useGenerateProcedureContent} from 'app/procedures/api';
import {
  useAccountSettingsState,
  useUpdateAccountSettings,
} from 'app/settings/api';
import {HOME_WELCOME_INVITATIONS} from 'app/urls';
import {getContentType} from 'app/utils/content-type';
import {gt, ngt} from 'app/utils/i18n';
import {useAsyncActions} from 'app/utils/redux';

import {PolicyDefinition, ProcedureDefinition} from './Definitions';
import {FullDocItem} from './FullDocItem';

import type {CreateNewDocProps} from 'app/general/types';
import type {DocumentSerializedType, ListResponseType} from 'sweet-types';

const DEFAULT_PROCEDURE_SUGGESTED_TITLES = [
  'Secure Payment Processing',
  'Product Delivery Tracking',
  'Customer Service Response',
];

const DEFAULT_POLICY_SUGGESTED_TITLES = [
  'Privacy Protection',
  'Return and Refund',
  'Product Information Accuracy',
];

type Props = {
  onNext: (location: string) => unknown;
};
export const Suggestions = ({onNext}: Props) => {
  const {account_id, industry_classification} = useAccountSettingsState().data!;
  const [createSuggestions] = useCreateSuggestions();
  const [updateAccountSettings] = useUpdateAccountSettings();
  const [suggestionURL, setSuggestionURL] = useState<string | undefined>(
    undefined
  );
  const {data: suggestion} = useSuggestion(suggestionURL ?? '', {
    skip: suggestionURL == null,
    refetchOnMountOrArgChange: false,
  });

  const [procedures, setProcedures] = useState<Array<string>>(
    DEFAULT_PROCEDURE_SUGGESTED_TITLES
  );
  const [policies, setPolicies] = useState<Array<string>>(
    DEFAULT_POLICY_SUGGESTED_TITLES
  );

  const [businessCategory, setBusinessCategory] = useState<string>(
    industry_classification || 'online shopping'
  );

  useEffect(() => {
    if (suggestion != null) {
      setProcedures(extractSuggestionsOfType(suggestion.results, 'procedure'));
      setPolicies(extractSuggestionsOfType(suggestion.results, 'policy'));
    }
  }, [suggestion]);

  const doCreateSuggestions = (prompt) => {
    updateAccountSettings({account_id, industry_classification: prompt});
    return createSuggestions({prompt, content_types: ['procedure', 'policy']})
      .unwrap()
      .then(({result}) => setSuggestionURL(result.url));
  };

  const {data: aiDocs} = useDocuments(
    convertLocationParamsToFetchParams({
      folder: '',
      filter: 'is_ai_generated',
      ordering: '-created_at',
      page_size: 10,
    })
  );

  return (
    <>
      <div className="row">
        <div className="col">
          <div className="lead">
            {gt(
              "Let's kick start your documentation with the power of Artificial Intelligence."
            )}{' '}
            {gt(
              'Sit back and let the AI suggest (and write) %(procedure)s and %(policy)s that would be useful to a business like yours.',
              {procedure: <ProcedureDefinition />, policy: <PolicyDefinition />}
            )}
            <form
              className="form-inline mt-4 mb-1"
              onSubmit={(e) => e.preventDefault()}
            >
              <label htmlFor="category" className="mr-2">
                {gt('Suggest policies and procedures for a business in')}
              </label>
              <input
                id="category"
                className="mr-2"
                type="text"
                value={businessCategory}
                onChange={(e) => setBusinessCategory(e.target.value)}
              />
              <StateButton
                type="submit"
                disabled={businessCategory.length === 0}
                busyText={gt('Suggesting...')}
                className="btn btn btn-primary"
                onClick={() => doCreateSuggestions(businessCategory)}
              >
                {gt('Suggest')}
              </StateButton>
            </form>
          </div>
        </div>
      </div>
      <div className="row">
        <div className="col">
          <ul className="list-group mt-4">
            {procedures.map((doc, i) => (
              <DocItem
                key={`${doc}-${i}`}
                doc={doc}
                content_type="procedure"
                aiDocs={aiDocs}
              />
            ))}
            {policies.map((doc, i) => (
              <DocItem
                key={`${doc}-${i}`}
                doc={doc}
                content_type="policy"
                aiDocs={aiDocs}
              />
            ))}
          </ul>
          <ul className="list-group mt-4">
            {aiDocs != null &&
              aiDocs.results.map((doc) => (
                <FullDocItem key={doc.url} object={doc} />
              ))}
          </ul>
        </div>
      </div>

      <div className="row mt-4 justify-content-end">
        <div className="col-sm-auto">
          <LazyTooltip
            tooltip={gt('Start using SweetProcess with an empty account.')}
          >
            <button
              className="btn text-secondary btn-link mr-2"
              onClick={() => onNext(HOME_WELCOME_INVITATIONS)}
            >
              {gt('Skip')}
            </button>
          </LazyTooltip>
          <StateButton
            disabled={aiDocs?.count === 0}
            busyText={gt('Setting up account...')}
            className="btn btn btn-primary"
            onClick={() => {
              onNext(HOME_WELCOME_INVITATIONS);
            }}
          >
            {ngt('Add Document', 'Add Documents', aiDocs?.count || 0)}
          </StateButton>
        </div>
      </div>
    </>
  );
};

type SelectProps = {
  doc: string;
  content_type: 'procedure' | 'policy';
  aiDocs?: ListResponseType<DocumentSerializedType>;
};

const DocItem = ({doc, content_type, aiDocs}: SelectProps) => {
  const ct = getContentType(content_type);
  const [createPolicy] = useCreatePolicy();
  const [url, setURL] = useState<string | undefined>(undefined);
  const [isError, setIsError] = useState<boolean>(false);

  const generateProcedureContent = useGenerateProcedureContent();
  const generatePolicyContent = useGeneratePolicyContent();

  const createNewPolicy = (props: CreateNewDocProps) =>
    createPolicy(props).unwrap();

  const create = useAsyncActions({
    // $FlowFixMe yes flow they will return promises
    procedure: createNewProcedure,
  });

  function onSuccess(job, element) {
    if (job.status === 'completed') {
      setURL(job.result.url);
    } else {
      setURL(element.current_version.html_url);
    }
  }

  if (
    url &&
    aiDocs?.results.filter((aidoc) =>
      aidoc.document.current_version.html_url.endsWith(url)
    ).length
  ) {
    // don't show this component when the ai generated doc is available.
    return null;
  }

  const onError = (url) => {
    setURL(url);
    setIsError(true);
  };

  const writeDoc = () => {
    const promise = (
      content_type == 'procedure' ? create.procedure : createNewPolicy
    )({
      name: doc,
      teams: [],
      folders: [],
    });
    return generateNewDocumentContent({
      createPromise: promise,
      generateContent:
        content_type === 'procedure'
          ? generateProcedureContent
          : content_type === 'policy'
          ? generatePolicyContent
          : generateProcedureContent,
      onSuccess,
      onError,
    });
  };

  return (
    <li className="list-group-item list-group-item-action flex-column align-items-start">
      <div className="d-flex w-100 justify-content-between">
        {url ? (
          <Link
            to={url}
            className="text-dark font-weight-bold d-block d-flex w-100"
          >
            <StyledCompactItemHeaderSpan>
              <DocumentIconWithNotifications
                contentType={content_type}
                notifications={undefined}
              />

              {doc}
            </StyledCompactItemHeaderSpan>
          </Link>
        ) : (
          <h5 className={cs({'text-danger': isError})}>
            <i className={`mr-1 ${ct.icon}`} />
            {doc}
          </h5>
        )}
        <div className="col-auto order-lg-5">
          {url ? (
            <button
              className={cs('btn btn-link', {
                'text-muted': !isError,
                'text-danger': isError,
              })}
              disabled={true}
            >
              {isError
                ? gt('Blank %(doc)s created.', {doc: content_type})
                : gt('%(icon)s complete', {
                    icon: <i className="fa fa-check-square mr-1" />,
                  })}
            </button>
          ) : (
            <StateButton
              className="btn btn-link"
              busyText={gt('Writing %(doc)s...', {doc: content_type})}
              onClick={writeDoc}
            >
              <i className="fa fa-plus mr-2" />
              {gt('Add to my account')}
            </StateButton>
          )}
        </div>
      </div>
    </li>
  );
};
